import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Table, Alert, Spinner, Nav, Button, Modal, Badge, Form, FloatingLabel, Offcanvas, ListGroup } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';
import UserAvatar from '../components/UserAvatar';
import { useInstitutionalData, useInstitutionalResources } from '../contexts/InstitutionalDataContext';

const InstitutionalUserProgramManagementPage = () => {
  const { invalidate } = useInstitutionalData();
  const { entries, loading: resourcesLoading, error: resourceError } = useInstitutionalResources([
    'institution',
    'programs',
    'users',
    'analytics',
  ]);
  const institution = entries.institution?.data || null;
  const programs = entries.programs?.data || [];
  const users = entries.users?.data || [];
  const analytics = entries.analytics?.data || null;
  const [error, setError] = useState('');
  const loading = resourcesLoading && !institution;
  const [activeTab, setActiveTab] = useState('programs');

  // Table UX (search + pagination)
  const [programsQuery, setProgramsQuery] = useState('');
  const [programsPage, setProgramsPage] = useState(1);
  const [programsPageSize, setProgramsPageSize] = useState(10);

  const [usersQuery, setUsersQuery] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(10);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showProgramDetails, setShowProgramDetails] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  
  const [showCreateProgram, setShowCreateProgram] = useState(false);
  const [createProgramData, setCreateProgramData] = useState({
    name: '',
    specialty: '',
    duration: '',
    maxPGYLevel: '',
    residentCount: '',
    selectedPlan: '',
    directorFirstName: '',
    directorLastName: '',
    directorEmail: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || role !== 'institutional_admin') {
      window.location.replace('/');
    }
  }, []);

  useEffect(() => {
    if (resourceError) setError(resourceError);
  }, [resourceError]);

  const refreshInstitutionalData = () => {
    invalidate(['institution', 'programs', 'users', 'analytics']);
  };

  const breadcrumbItems = [
    { href: '/institutional-admin/dashboard', label: 'Dashboard' },
    { label: 'User/program management' }
  ];

  useEffect(() => {
    setProgramsPage(1);
  }, [programsQuery, programsPageSize]);

  useEffect(() => {
    setUsersPage(1);
  }, [usersQuery, usersPageSize]);

  const qNorm = (v) => String(v ?? '').toLowerCase();
  const programsQueryNorm = programsQuery.trim().toLowerCase();
  const usersQueryNorm = usersQuery.trim().toLowerCase();

  const programsFiltered = programsQueryNorm
    ? programs.filter((p) => {
        const directorName = p.director ? `${p.director.firstName} ${p.director.lastName}` : '';
        return [
          p.id,
          p.name,
          p.specialty,
          directorName,
          p.director?.email,
        ].some((f) => qNorm(f).includes(programsQueryNorm));
      })
    : programs;

  const programsTotalPages = Math.max(1, Math.ceil(programsFiltered.length / programsPageSize));
  const programsPageSafe = Math.min(programsPage, programsTotalPages);
  const programsPageStart = (programsPageSafe - 1) * programsPageSize;
  const programsPageItems = programsFiltered.slice(programsPageStart, programsPageStart + programsPageSize);

  const usersFiltered = usersQueryNorm
    ? users.filter((u) => {
        const fullName = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
        return [
          u.id,
          fullName,
          u.email,
          u.role,
          u.program?.name,
        ].some((f) => qNorm(f).includes(usersQueryNorm));
      })
    : users;

  const usersTotalPages = Math.max(1, Math.ceil(usersFiltered.length / usersPageSize));
  const usersPageSafe = Math.min(usersPage, usersTotalPages);
  const usersPageStart = (usersPageSafe - 1) * usersPageSize;
  const usersPageItems = usersFiltered.slice(usersPageStart, usersPageStart + usersPageSize);

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/institutional-admin/programs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(createProgramData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create program');
      }

      setShowCreateProgram(false);
      setCreateProgramData({
        name: '',
        specialty: '',
        duration: '',
        maxPGYLevel: '',
        residentCount: '',
        selectedPlan: '',
        directorFirstName: '',
        directorLastName: '',
        directorEmail: ''
      });
      refreshInstitutionalData();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleDateString();
  };

  const formatPhone = (value) => {
    if (!value) return 'N/A';
    const cleaned = String(value).replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    return value;
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (typeof address === 'string') return address.trim() || 'N/A';
    if (typeof address !== 'object') return 'N/A';
    const street = [address.street, address.apt ? `Apt ${address.apt}` : ''].filter(Boolean).join(' ');
    const cityStateZip = [address.city, address.state, address.zip].filter(Boolean).join(' ');
    return [street, cityStateZip].filter(Boolean).join(', ') || 'N/A';
  };

  const humanizeRole = (role) => String(role || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const getAnticipatedGraduationYear = (user) => {
    const explicitGraduationYear = Number(user?.graduationYear);
    if (Number.isFinite(explicitGraduationYear) && explicitGraduationYear > 1900) {
      return explicitGraduationYear;
    }
    const pgyLevel = Number(user?.year);
    const programDuration = Number(user?.program?.duration || user?.program?.maxPGYLevel);
    if (!Number.isFinite(pgyLevel) || !Number.isFinite(programDuration)) return null;
    return new Date().getFullYear() + Math.max(programDuration - pgyLevel, 0);
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const openProgramDetails = (program) => {
    setSelectedProgram(program);
    setShowProgramDetails(true);
  };

  if (loading) {
    return (
      <div className="d-flex vh-100 overflow-hidden bg-light">
        <SuperAdminSidebar variant="institutional" />
        <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto justify-content-center align-items-center">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

    return (
      <div className="d-flex vh-100">
      <SuperAdminSidebar variant="institutional" />
      {/* Main content: top bar + content area */}
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto">
        <AdminTopBar variant="institutional" breadcrumbItems={breadcrumbItems} />
        {/* Content area with left tabs + main */}
        <div className="flex-grow-1">
          <Container fluid>
            <Row>
              <Col xl={2} lg={3} md={3} sm={4} className="border-end bg-inverse-light pt-3 adjust-min-vh">
                <div className="d-flex align-items-center gap-2 mb-3 px-2">
                  {institution?.logoUrl && (
                    <img
                      src={/^https?:\/\//i.test(institution.logoUrl) ? institution.logoUrl : `${API_BASE_URL}${institution.logoUrl}`}
                      alt=""
                      style={{ height: 36, width: 36, objectFit: 'contain', borderRadius: 6 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="small">
                    <div className="fw-semibold text-truncate" style={{ maxWidth: 160 }}>{institution?.name}</div>
                    <div className="text-muted">Institution</div>
                  </div>
                </div>
                <Nav variant="secondary-tabs" className="flex-column" activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'programs')}>
                  <Nav.Item>
                    <Nav.Link eventKey="programs" className="d-flex align-items-center justify-content-between">Programs <Badge bg="secondary" className="ms-1">{programs.length}</Badge></Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="users" className="d-flex align-items-center justify-content-between">Users <Badge bg="secondary" className="ms-1">{users.length}</Badge></Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="analytics" className="d-flex align-items-center justify-content-between">Analytics</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col 
                xl={10} lg={9} md={9} sm={8} 
                className="adjust-min-vh"
              >
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

         {activeTab === 'programs' && (
            <Row className="h-100">
              <Col md={12} className="p-0">
                <Card className="p-0 border-0 rounded-0 h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center gap-3 px-4 py-3 flex-wrap custom-sticky-top border-bottom-0">
                    <div className="d-flex flex-column">
                      <h6 className="mb-0">Programs</h6>
                      <div className="text-muted small">
                        {programsFiltered.length > programsPageSize
                          ? <>Showing {programsPageStart + 1}–{Math.min(programsPageStart + programsPageItems.length, programsFiltered.length)} of {programsFiltered.length} programs</>
                          : <>Showing {programsFiltered.length} program{programsFiltered.length !== 1 ? 's' : ''}</>
                        }
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end ms-auto">
                      <Form.Control
                        size="sm"
                        placeholder="Search programs…"
                        value={programsQuery}
                        onChange={(e) => setProgramsQuery(e.target.value)}
                        style={{ width: 220 }}
                      />
                      <Form.Select
                        size="sm"
                        value={programsPageSize}
                        onChange={(e) => setProgramsPageSize(Number(e.target.value))}
                        style={{ width: 110 }}
                        aria-label="Programs page size"
                      >
                        <option value={5}>5 / page</option>
                        <option value={10}>10 / page</option>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                      </Form.Select>
                      <Button variant="primary" size="sm" onClick={() => setShowCreateProgram(true)}>
                        Create New Program
                      </Button>
                    </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                    <div className=" table-responsive alt-table-striped programs-table-scroll-wrapper">
                      <Table hover className="table-header-sticky mb-0">
                      <thead>
                        <tr>
                          <th>Program Name</th>
                          <th>Specialty</th>
                          <th>Director</th>
                          <th>Residents</th>
                          <th>Attendings</th>
                          <th>Students</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {programsPageItems.map(program => (
                          <tr key={program.id} onClick={() => openProgramDetails(program)} style={{ cursor: 'pointer' }}>
                            <td>{program.name}</td>
                            <td>{program.specialty || 'Not specified'}</td>
                            <td>
                              {program.director ? (
                                `${program.director.firstName} ${program.director.lastName}`
                              ) : 'No director assigned'}
                            </td>
                            <td><Badge bg="info">{program.residentCount}</Badge></td>
                            <td><Badge bg="warning">{program.attendingCount}</Badge></td>
                            <td><Badge bg="secondary">{program.studentCount}</Badge></td>
                            <td>
                              <Badge bg={program.director?.isActive ? 'success' : 'danger'}>
                                {program.director?.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    </div>
                  </Card.Body>
                  <Card.Footer className='bg-white p-0'>
                          <div className=" d-flex justify-content-end border-top align-items-center gap-2 p-2 px-4">
              <Button
                        variant="light-lite"
                        size="sm"
                        disabled={programsPageSafe <= 1}
                        onClick={() => setProgramsPage((p) => Math.max(1, p - 1))}
                      >
                        Prev
                      </Button>
                      <div className="small text-muted px-1" style={{ minWidth: 90, textAlign: 'center' }}>
                        Page {programsPageSafe} / {programsTotalPages}
                      </div>
                      <Button
                        variant="light-lite"
                        size="sm"
                        disabled={programsPageSafe >= programsTotalPages}
                        onClick={() => setProgramsPage((p) => Math.min(programsTotalPages, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                    </Card.Footer>
                </Card>
              </Col>
             </Row>
         )}

         {activeTab === 'users' && (
            <Row className="h-100">
              <Col md={12} className="p-0 overflow-hidden">
                <Card className="p-0 border-0 rounded-0 h-100">
                  <Card.Header className="d-flex px-4 py-3 justify-content-between align-items-center custom-sticky-top border-bottom-0">
                    <div className="d-flex flex-column">
                    <h6 className="mb-0">All Users</h6>
                    <div className="text-muted small">
                      {usersFiltered.length > usersPageSize
                        ? <>Showing {usersPageStart + 1}–{Math.min(usersPageStart + usersPageItems.length, usersFiltered.length)} of {usersFiltered.length} users</>
                        : <>Showing {usersFiltered.length} user{usersFiltered.length !== 1 ? 's' : ''}</>
                      }
                    </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end ms-auto">
                      <Form.Control
                        size="sm"
                        placeholder="Search users…"
                        value={usersQuery}
                        onChange={(e) => setUsersQuery(e.target.value)}
                        style={{ width: 220 }}
                      />
                      <Form.Select
                        size="sm"
                        value={usersPageSize}
                        onChange={(e) => setUsersPageSize(Number(e.target.value))}
                        style={{ width: 110 }}
                        aria-label="Users page size"
                      >
                        <option value={10}>10 / page</option>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </Form.Select>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className=" table-responsive alt-table-striped users-table-scroll-wrapper">
                      <Table hover className="table-header-sticky mb-0">
                        <thead>
                          <tr className="custom-header-bg">
                            <th>UID</th>
                            <th className="text-start">Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Program</th>
                            <th>Status</th>
                            <th>Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersPageItems.map(user => (
                            <tr key={user.id} onClick={() => openUserDetails(user)} style={{ cursor: 'pointer' }}>
                              <td>{user.id}</td>
                              <td className="text-start">{user.firstName} {user.lastName}</td>
                              <td>{user.email}</td>
                              <td><Badge bg="primary">{user.role}</Badge></td>
                              <td>{user.program?.name || 'No program'}</td>
                              <td>
                                <Badge bg={user.isActive ? 'success' : 'danger'}>
                                  {user.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                
                  </Card.Body>
                  <Card.Footer className='bg-white p-0'> 
                    <div className=" d-flex justify-content-end border-top align-items-center gap-2 p-2 px-4">
                    <Button
                        variant="light-solid"
                        size="sm"
                        disabled={usersPageSafe <= 1}
                        onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                      >
                        Prev
                      </Button>
                      <div className="small text-muted px-1" style={{ minWidth: 90, textAlign: 'center' }}>
                        Page {usersPageSafe} / {usersTotalPages}
                      </div>
                      <Button
                        variant="light-solid"
                        size="sm"
                        disabled={usersPageSafe >= usersTotalPages}
                        onClick={() => setUsersPage((p) => Math.min(usersTotalPages, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                    </Card.Footer>
                </Card>
              </Col>
             </Row>
         )}

         {activeTab === 'analytics' && (
            <Row>
              <Col md={12}>
                <Card>
                  <Card.Header>
                    <h5>Institution Analytics</h5>
                  </Card.Header>
                  <Card.Body>
                    {analytics && (
                      <Row>
                        <Col md={3}>
                          <div className="text-center">
                            <h3 className="text-primary">{analytics.summary.totalPrograms}</h3>
                            <p>Total Programs</p>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h3 className="text-success">{analytics.summary.totalUsers}</h3>
                            <p>Total Users</p>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h3 className="text-info">{analytics.summary.totalResidents}</h3>
                            <p>Residents</p>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h3 className="text-warning">{analytics.summary.totalAttendings}</h3>
                            <p>Attendings</p>
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              </Col>
             </Row>
         )}
          </Col>
            </Row>
          </Container>
        </div>
      </div>

      {/* Create Program Modal */}
      <Offcanvas
        show={showUserDetails}
        onHide={() => setShowUserDetails(false)}
        placement="end"
        className="w-100"
        style={{ maxWidth: 480 }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>User Details</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body >
          {selectedUser && (
            <>
              <div className="d-flex flex-column small">
                <Card className="border-0 bg-light rounded-4 mb-3">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-start gap-3">
                      <UserAvatar user={selectedUser} size={64}/>
                      <div className="flex-grow-1">
                        <h5 className="mb-0 fw-semibold">{selectedUser.firstName} {selectedUser.middleName || ''} {selectedUser.lastName}</h5>
                        <div className="text-primary">
                          Medical Doctor, {selectedUser.specialty || selectedUser.program?.specialty || selectedUser.program?.name || humanizeRole(selectedUser.role)}
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                          <Badge pill bg="light" text="dark" className="border">
                            {selectedUser.year ? `PGY-${selectedUser.year}` : 'N/A'}
                          </Badge>
                          <Badge pill bg="light" text="dark" className="border">
                            Grad: {getAnticipatedGraduationYear(selectedUser) ? `June ${getAnticipatedGraduationYear(selectedUser)}` : 'N/A'}
                          </Badge>
                          <Badge pill bg={selectedUser.isActive ? 'success' : 'secondary'}>
                            {selectedUser.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border-0 mb-3">
                  <Card.Body className="p-2">
                    <div className="text-uppercase fw-semibold text-muted small mb-3" style={{ letterSpacing: '.06em' }}>Contact</div>
                    <Row className="g-1">
                      <Col xs={6}>
                        <div className="text-muted fw-semibold">Email</div>
                        <div className="text-primary fw-semibold">{selectedUser.email || 'N/A'}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted fw-semibold">Phone</div>
                        <div className="fw-semibold">{formatPhone(selectedUser.phone)}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted fw-semibold">Pager number</div>
                        <div className="fw-semibold">{formatPhone(selectedUser.pagerNumber)}</div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="border-0 mb-3">
                  <Card.Body className="p-2">
                    <div className="text-uppercase fw-semibold text-muted small mb-3" style={{ letterSpacing: '.06em' }}>Account & Timeline</div>
                    <Row className="g-1">
                      <Col xs={6}><div className="text-muted fw-semibold">Joined</div><div className="fw-semibold">{formatDate(selectedUser.createdAt)}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Account status</div><div><Badge pill bg={selectedUser.isActive ? 'success' : 'secondary'}>{selectedUser.isActive ? 'Active' : 'Inactive'}</Badge></div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Profile completed</div><div className="fw-semibold">{selectedUser.profileCompleted ? 'Yes' : 'No'}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Last login</div><div className="fw-semibold">{formatDate(selectedUser.lastLogin)}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Start date</div><div className="fw-semibold">{formatDate(selectedUser.startDate)}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">End date</div><div className="fw-semibold">{formatDate(selectedUser.endDate)}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Role</div><div className="fw-semibold">{humanizeRole(selectedUser.role)}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Program</div><div className="fw-semibold">{selectedUser.program?.name || 'N/A'}</div></Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="border-0 mb-3">
                  <Card.Body className="p-2">
                    <div className="text-uppercase fw-semibold text-muted small mb-3" style={{ letterSpacing: '.06em' }}>Personal</div>
                    <Row className="g-1">
                      <Col xs={6}><div className="text-muted fw-semibold">Date of birth</div><div className="fw-semibold">{formatDate(selectedUser.dob)}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Gender</div><div className="fw-semibold">{selectedUser.gender || 'N/A'}</div></Col>
                      <Col xs={12}><div className="text-muted fw-semibold">Home address</div><div className="fw-semibold">{formatAddress(selectedUser.address)}</div></Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="border-0 mb-3">
                  <Card.Body className="p-2">
                    <div className="text-uppercase fw-semibold text-muted small mb-3" style={{ letterSpacing: '.06em' }}>Additional Info</div>
                    <Row className="g-1">
                      <Col xs={6}><div className="text-muted fw-semibold">NPI number</div><div className="fw-semibold">{selectedUser.npiNumber || 'N/A'}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">License number</div><div className="fw-semibold">{selectedUser.licenseNumber || 'N/A'}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Department</div><div className="fw-semibold">{selectedUser.department || 'N/A'}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Position title</div><div className="fw-semibold">{selectedUser.positionTitle || 'N/A'}</div></Col>
                      <Col xs={6}><div className="text-muted fw-semibold">Board certified</div><div><Badge pill bg={selectedUser.boardCertified ? 'success' : 'secondary'}>{selectedUser.boardCertified ? 'Yes' : 'No'}</Badge></div></Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Alert variant="danger" className="rounded-4 mb-3">
                    <div className="text-uppercase fw-semibold small mb-3 text-danger" style={{ letterSpacing: '.06em' }}>Emergency Contact</div>
                    <Row className="g-1">
                      <Col xs={6}>
                        <div className="text-danger-emphasis fw-semibold">Name</div>
                        <div className="fw-semibold text-danger-emphasis">{selectedUser.emergencyContact?.name || 'N/A'}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-danger-emphasis fw-semibold">Relation</div>
                        <div className="fw-semibold text-danger-emphasis">{selectedUser.emergencyContact?.relation || 'N/A'}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-danger-emphasis fw-semibold">Phone</div>
                        <div className="fw-semibold text-danger-emphasis">{formatPhone(selectedUser.emergencyContact?.phone)}</div>
                      </Col>
                    </Row>
                  </Alert>
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Offcanvas
        show={showProgramDetails}
        onHide={() => setShowProgramDetails(false)}
        placement="end"
        className="w-100"
        style={{ maxWidth: 500 }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Program Details</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {selectedProgram && (
            <>
              <div className="mb-3">
                <h5 className="fw-semibold mb-1">{selectedProgram.name || 'Program'}</h5>
                <div className="text-muted mb-2">{selectedProgram.specialty || 'Specialty not specified'}</div>
                <Badge pill bg={selectedProgram.director?.isActive ? 'success' : 'secondary'}>
                  {selectedProgram.director?.isActive ? 'Director active' : 'Director inactive'}
                </Badge>
              </div>

              <Card className="mb-3 rounded-4">
                <Card.Body className="p-3">
                  <div className="text-uppercase fw-semibold text-muted small mb-2" style={{ letterSpacing: '.06em' }}>
                    Program Information
                  </div>
                  {[
                    ['Program ID', selectedProgram.id || 'N/A'],
                    ['Duration', `${selectedProgram.duration || 'N/A'}${selectedProgram.duration ? ' years' : ''}`],
                    ['Max PGY', selectedProgram.maxPGYLevel || 'N/A'],
                    ['Selected plan', selectedProgram.selectedPlan || 'N/A'],
                    ['Created', formatDate(selectedProgram.createdAt)],
                  ].map(([label, value], idx, arr) => (
                    <div
                      key={label}
                      className={`d-flex justify-content-between align-items-center py-2 small ${idx < arr.length - 1 ? 'border-bottom pb-2' : ''}`}>
                    <span className="fw-semibold">{label}</span>
                    <span className="fw-semibold">{value}</span>
                  </div>
                ))}
                </Card.Body>
              </Card>

              <Card className="border mb-3 rounded-4">
                <Card.Body className="p-3">
                  <div className="text-uppercase fw-semibold text-muted small mb-3" style={{ letterSpacing: '.06em' }}>
                    Program Director
                  </div>
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <UserAvatar
                        user={selectedProgram.director || { firstName: 'N', lastName: 'A' }}
                        size={40}
                      />
                      <div>
                        <div className="fw-semibold">
                          {selectedProgram.director
                            ? `${selectedProgram.director.firstName} ${selectedProgram.director.lastName}`
                            : 'No director assigned'}
                        </div>
                        <div className="text-primary small">{selectedProgram.director?.email || 'N/A'}</div>
                      </div>
                    </div>
                    <Badge pill bg={selectedProgram.director?.isActive ? 'success' : 'secondary'}>
                      {selectedProgram.director?.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>

              <Card className="border rounded-4">
                <Card.Body className="p-3">
                  <div className="text-uppercase fw-semibold text-muted small mb-3" style={{ letterSpacing: '.06em' }}>
                    Program Census
                  </div>
                  <Row className="g-2">
                    <Col xs={4}>
                      <div className="text-center p-2 rounded-3 bg-light">
                        <div className="fw-bold fs-2 text-primary">{selectedProgram.residentCount || 0}</div>
                        <div className="small fw-semibold text-muted">Residents</div>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="text-center p-2 rounded-3 bg-light">
                        <div className="fw-bold fs-2 text-warning">{selectedProgram.attendingCount || 0}</div>
                        <div className="small fw-semibold text-muted">Attendings</div>
                      </div>
                    </Col>
                    <Col xs={4}>
                      <div className="text-center p-2 rounded-3 bg-light">
                        <div className="fw-bold fs-2 text-info">{selectedProgram.studentCount || 0}</div>
                        <div className="small fw-semibold text-muted">Students</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Modal show={showCreateProgram} onHide={() => setShowCreateProgram(false)} size="lg">
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-50"><span>Create New Program</span>
              <div className="text-hl"></div></Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateProgram}>
          <Modal.Body className="px-4">
            <Row>
              <div className="d-flex gap-2">
              <h6>Program Details</h6>
              </div>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingProgramName" label="Program Name" className="required-field mb-0">
                    <Form.Control
                      type="text"
                      value={createProgramData.name}
                      onChange={(e) => setCreateProgramData({ ...createProgramData, name: e.target.value })}
                      required
                      placeholder="Enter program name"
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingSpecialty" label="Specialty" className="mb-0">
                    <Form.Select
                      value={createProgramData.specialty}
                      onChange={(e) => setCreateProgramData({ ...createProgramData, specialty: e.target.value })}
                      aria-label="Select medical specialty"
                    >
                      <option value="">Select specialty</option>
                      <option value="Anesthesiology">Anesthesiology</option>
                      <option value="Dermatology">Dermatology</option>
                      <option value="Diagnostic Radiology">Diagnostic Radiology</option>
                      <option value="Emergency Medicine">Emergency Medicine</option>
                      <option value="Family Medicine">Family Medicine</option>
                      <option value="Internal Medicine">Internal Medicine</option>
                      <option value="Neurology">Neurology</option>
                      <option value="Obstetrics and Gynecology">Obstetrics and Gynecology</option>
                      <option value="Ophthalmology">Ophthalmology</option>
                      <option value="Orthopaedic Surgery">Orthopaedic Surgery</option>
                      <option value="Otolaryngology">Otolaryngology</option>
                      <option value="Pathology">Pathology</option>
                      <option value="Pediatrics">Pediatrics</option>
                      <option value="Physical Medicine and Rehabilitation">Physical Medicine and Rehabilitation</option>
                      <option value="Plastic Surgery">Plastic Surgery</option>
                      <option value="Psychiatry">Psychiatry</option>                      
                      <option value="General Surgery">General Surgery</option>
                      <option value="Thoracic Surgery">Thoracic Surgery</option>
                      <option value="Urology">Urology</option>
                      <option value="Vascular Surgery">Vascular Surgery</option>
                      <option value="Cardiology">Cardiology</option>
                      <option value="Endocrinology">Endocrinology</option>
                      <option value="Gastroenterology">Gastroenterology</option>
                      <option value="Hematology">Hematology</option>
                      <option value="Hepatology">Hepatology</option>
                      <option value="Immunology">Immunology</option>
                      <option value="Nephrology">Nephrology</option>
                      <option value="Oncology">Oncology</option>
                      <option value="Pulmonology">Pulmonology</option>
                      <option value="Rheumatology">Rheumatology</option>
                      <option value="Radiology">Radiology</option>
                      <option value="Rheumatology">Rheumatology</option>
                      <option value="Other">Other</option>
                      {/* Add more specialties as needed */}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingDuration" label="Duration (years)" className="mb-0">
                    <Form.Control
                      type="number"
                      value={createProgramData.duration}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCreateProgramData({
                          ...createProgramData,
                          duration: value,
                          maxPGYLevel: value,
                        });
                      }}
                      placeholder="Enter duration in years"
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingMaxPGYLevel" label="Max PGY Level" className="mb-0">
                    <Form.Control
                      type="number"
                      value={createProgramData.maxPGYLevel}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCreateProgramData({
                          ...createProgramData,
                          maxPGYLevel: value,
                          duration: value,
                        });
                      }}
                      placeholder="Enter max PGY level"
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
            <hr />
            <h6>Program Director Details</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingDirectorFirstName" label="First Name" className="required-field mb-0">
                  <Form.Control
                    type="text"
                    value={createProgramData.directorFirstName}
                    onChange={(e) => setCreateProgramData({...createProgramData, directorFirstName: e.target.value})}
                    required
                    placeholder="Enter first name"
                  />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingDirectorLastName" label="Last Name" className="required-field mb-0">
                  <Form.Control
                    type="text"
                    value={createProgramData.directorLastName}
                    onChange={(e) => setCreateProgramData({...createProgramData, directorLastName: e.target.value})}
                    required
                    placeholder="Enter last name"
                  />
                  </FloatingLabel>
                  </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 mb-md-0">
                  <FloatingLabel controlId="floatingDirectorEmail" label="Email" className="required-field mb-0">
                  <Form.Control
                    type="email"
                    value={createProgramData.directorEmail}
                    onChange={(e) => setCreateProgramData({...createProgramData, directorEmail: e.target.value})}
                    required
                    placeholder="Enter email"
                  />
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="px-4 border-top-0">
            <Button variant="light" onClick={() => setShowCreateProgram(false)}>
              Cancel
            </Button>
            <Button variant="success" type="submit">
              Create Program
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </div>
  );
};

export default InstitutionalUserProgramManagementPage;
